import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = "543704504444";

const initialForm = { nombre: "", email: "", telefono: "", asunto: "", mensaje: "" };

export default function Contacto() {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.email || !form.mensaje) {
            toast.error("Completá nombre, email y mensaje.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/contact`, form);
            if (data && data.id) {
                toast.success("¡Mensaje enviado! Te contactaremos a la brevedad.");
                setForm(initialForm);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "No pudimos enviar tu mensaje. Probá de nuevo o escribinos por WhatsApp.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contacto" className="relative py-24 md:py-32 bg-[#0A0A0A] border-t border-[#1a1a1a]" data-testid="contacto-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <p className="overline" data-testid="contacto-overline">Contacto</p>
                    <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]" style={{ fontFamily: "Chivo" }} data-testid="contacto-title">
                        Hablemos<br />
                        de tu <span className="text-[#14888F]">próxima obra</span>.
                    </h2>
                    <p className="mt-6 text-white/65 text-base leading-relaxed max-w-md">
                        Contanos los detalles del proyecto y te responderemos con una propuesta técnica clara.
                    </p>

                    <div className="mt-10 space-y-5">
                        <a
                            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hola RAMSOL, quisiera consultar por un proyecto.")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-4 p-4 border border-[#262626] hover:border-[#14888F] transition-colors group"
                            data-testid="contact-whatsapp"
                        >
                            <span className="w-11 h-11 bg-[#14888F]/10 border border-[#14888F]/30 flex items-center justify-center group-hover:bg-[#14888F] transition-colors">
                                <MessageCircle size={18} className="text-[#14888F] group-hover:text-white" />
                            </span>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">WhatsApp</p>
                                <p className="text-white font-bold text-base mt-0.5">+54 9 370 450-4444</p>
                            </div>
                        </a>

                        <a
                            href="mailto:ramirez.arq1@gmail.com"
                            className="flex items-center gap-4 p-4 border border-[#262626] hover:border-[#14888F] transition-colors group"
                            data-testid="contact-email"
                        >
                            <span className="w-11 h-11 bg-[#14888F]/10 border border-[#14888F]/30 flex items-center justify-center group-hover:bg-[#14888F] transition-colors">
                                <Mail size={18} className="text-[#14888F] group-hover:text-white" />
                            </span>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Email</p>
                                <p className="text-white font-bold text-base mt-0.5">ramirez.arq1@gmail.com</p>
                            </div>
                        </a>

                        <div className="flex items-center gap-4 p-4 border border-[#262626]" data-testid="contact-location">
                            <span className="w-11 h-11 bg-[#14888F]/10 border border-[#14888F]/30 flex items-center justify-center">
                                <MapPin size={18} className="text-[#14888F]" />
                            </span>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Cobertura</p>
                                <p className="text-white font-bold text-base mt-0.5">Argentina · Obras a nivel nacional</p>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="lg:col-span-7 bg-[#0E0E0E] border border-[#262626] p-6 md:p-10" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 block mb-2">Nombre*</label>
                            <input
                                type="text"
                                name="nombre"
                                value={form.nombre}
                                onChange={onChange}
                                required
                                placeholder="Tu nombre"
                                className="ramsol-input"
                                data-testid="form-input-nombre"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 block mb-2">Email*</label>
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                required
                                placeholder="tu@correo.com"
                                className="ramsol-input"
                                data-testid="form-input-email"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 block mb-2">Teléfono</label>
                            <input
                                type="tel"
                                name="telefono"
                                value={form.telefono}
                                onChange={onChange}
                                placeholder="+54 9 ..."
                                className="ramsol-input"
                                data-testid="form-input-telefono"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 block mb-2">Asunto</label>
                            <input
                                type="text"
                                name="asunto"
                                value={form.asunto}
                                onChange={onChange}
                                placeholder="Tipo de proyecto"
                                className="ramsol-input"
                                data-testid="form-input-asunto"
                            />
                        </div>
                    </div>
                    <div className="mt-5">
                        <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 block mb-2">Mensaje*</label>
                        <textarea
                            name="mensaje"
                            value={form.mensaje}
                            onChange={onChange}
                            rows={6}
                            required
                            placeholder="Contanos sobre tu proyecto, ubicación, plazos..."
                            className="ramsol-input resize-none"
                            data-testid="form-input-mensaje"
                        />
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <p className="text-xs text-white/45 max-w-xs">
                            Al enviar este formulario aceptás el tratamiento de tus datos para contacto comercial.
                        </p>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="form-submit-button"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {loading ? "Enviando..." : "Enviar Mensaje"}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
